import moment from 'moment';
import { normalizeCompareDateKey, normalizeTrendAnchor } from './statisticsCompareConfig';
import { createStatisticsFilterDefaults, pickStatisticsScopeFields } from './statisticsFilterParams';
import { normalizeExclusiveOrderStatusFilters } from './statisticsOrderStatusFilter';
import { resolveFilledStatisticsDatePeriodQuery } from './statisticsDatePeriods';

const ARRAY_SCOPE_KEYS = [
	'saleUser',
	'assistantUser',
	'suppliers',
	'brands',
	'categories',
	'products',
	'customers',
	'customerContacts',
	'customerProvince',
	'customerCity',
	'approvalStatuses',
	'primaryPhases',
	'paymentStatuses',
	'deliveryStatuses',
	'processNodes',
];

const DATE_SCOPE_KEYS = ['startDate', 'endDate', 'completedStartDate', 'completedEndDate'];
const COMPLETED_DATE_ANCHOR_URL_KEY = 'cdAnchor';
const DATE_PERIODS_URL_KEY = 'dPeriods';
const DATE_PERIOD_RELATIONS_URL_KEY = 'dPRels';

const FILTER_SCOPE_PARAM_KEYS = [
	...DATE_SCOPE_KEYS,
	...ARRAY_SCOPE_KEYS,
	'dateRelation',
	'processNodeRelations',
	'isCompleted',
	'statusFilterAdditive',
	'compareYoy',
	'compareMom',
];

/** 废弃参数：同步 URL 时剔除。含曾写入的制作区 trend 旋钮，避免旧书签污染总览 */
const LEGACY_FILTER_URL_KEYS_TO_STRIP = [
	'primaryPhaseLens',
	'lcAnchor',
	'lcPeriods',
	'lcMode',
	'lcMyears',
	'lcMyoy',
	'lcAxis',
	'lcDateField',
	'lcYmetric',
	'lcYmetrics',
	'lcChart',
	'lcShape',
	'lcBar',
	'lcSeries',
	'lcYcolors',
];

const ALL_FILTER_URL_PARAM_KEYS = [
	...FILTER_SCOPE_PARAM_KEYS,
	COMPLETED_DATE_ANCHOR_URL_KEY,
	DATE_PERIODS_URL_KEY,
	DATE_PERIOD_RELATIONS_URL_KEY,
];

const parseJsonParam = (raw) => {
	if (!raw) return null;
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
};

const serializeJsonParam = (value) => {
	if (value == null) return undefined;
	try {
		return JSON.stringify(value);
	} catch {
		return undefined;
	}
};

const serializeUrlDate = (value) => {
	if (!value) return undefined;
	const parsed = moment(value);
	return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};

const parseUrlDate = (value) => {
	if (!value) return null;
	const parsed = moment(value, ['YYYY-MM-DD', moment.ISO_8601], true);
	return parsed.isValid() ? parsed.toDate() : null;
};

const parseCommaList = (value) => {
	if (!value || value === '') return [];
	return String(value)
		.split(',')
		.map((item) => item.trim())
		.filter(Boolean);
};

const arraysEqual = (left = [], right = []) => {
	if (left.length !== right.length) return false;
	return [...left].sort().every((item, index) => item === [...right].sort()[index]);
};

const isDefaultScopeValue = (key, value, defaults) => {
	const fallback = defaults[key];
	if (DATE_SCOPE_KEYS.includes(key)) {
		return normalizeCompareDateKey(value) === normalizeCompareDateKey(fallback);
	}
	if (ARRAY_SCOPE_KEYS.includes(key)) {
		return arraysEqual(value, fallback ?? []);
	}
	if (key === 'dateRelation') return (value ?? 'and') !== 'or';
	if (key === 'processNodeRelations') {
		const list = Array.isArray(value) ? value : [];
		return list.length === 0 || list.every((item) => item !== 'or');
	}
	if (key === 'compareYoy') return value !== false;
	if (key === 'compareMom') return value !== false;
	if (key === 'isCompleted') return !value;
	if (key === 'statusFilterAdditive') return value !== true;
	if (key === 'completedDateAnchor') {
		return JSON.stringify(normalizeTrendAnchor(value)) === JSON.stringify(normalizeTrendAnchor(fallback));
	}
	return value === fallback;
};

/**
 * 顶栏 scope → URL query（省略与默认等价的项）
 * @param {{ forceDateScope?: boolean }} [options] 分享链接强制写入起止日期，避免默认「本年」被省略后新窗顶栏日期列空白
 */
export const scopeFieldsToSearchParams = (filters = {}, { forceDateScope = false } = {}) => {
	const defaults = createStatisticsFilterDefaults();
	const scope = pickStatisticsScopeFields(filters);
	const params = {};

	for (const key of FILTER_SCOPE_PARAM_KEYS) {
		const value = scope[key];
		const skipAsDefault = !(forceDateScope && DATE_SCOPE_KEYS.includes(key));
		if (skipAsDefault && isDefaultScopeValue(key, value, defaults)) continue;

		if (DATE_SCOPE_KEYS.includes(key)) {
			const serialized = serializeUrlDate(value);
			if (serialized) params[key] = serialized;
			continue;
		}

		if (ARRAY_SCOPE_KEYS.includes(key)) {
			if (Array.isArray(value) && value.length > 0) {
				params[key] = value.join(',');
			}
			continue;
		}

		if (key === 'dateRelation') {
			params.dateRelation = 'or';
			continue;
		}

		if (key === 'processNodeRelations') {
			if (Array.isArray(value) && value.some((item) => item === 'or')) {
				params.processNodeRelations = value.map((item) => (item === 'or' ? 'or' : 'and')).join(',');
			}
			continue;
		}

		if (key === 'compareYoy' && value === false) {
			params.compareYoy = 'false';
			continue;
		}

		if (key === 'compareMom' && value === false) {
			params.compareMom = 'false';
			continue;
		}

		if (key === 'isCompleted' && value) {
			params.isCompleted = value;
			continue;
		}

		if (key === 'statusFilterAdditive' && value === true) {
			params.statusFilterAdditive = 'true';
		}
	}

	const completedAnchor = normalizeTrendAnchor(scope.completedDateAnchor);
	if (!isDefaultScopeValue('completedDateAnchor', completedAnchor, defaults)) {
		const serialized = serializeJsonParam(completedAnchor);
		if (serialized) params[COMPLETED_DATE_ANCHOR_URL_KEY] = serialized;
	}

	const filledDatePeriods = resolveFilledStatisticsDatePeriodQuery(scope);
	if (filledDatePeriods.periods.length > 1) {
		const serialized = serializeJsonParam(
			filledDatePeriods.periods.map((period) => ({
				startDate: serializeUrlDate(period.startDate),
				endDate: serializeUrlDate(period.endDate),
			})),
		);
		if (serialized) params[DATE_PERIODS_URL_KEY] = serialized;
	}

	return params;
};

/** 顶栏已应用 scope → URL query（不含制作区 trend 旋钮） */
export const filtersToSearchParams = (filters = {}, options = {}) => scopeFieldsToSearchParams(filters, options);

/** URL query → 顶栏 scope 补丁（忽略已废弃的 lc* trend 参数） */
export const searchParamsToFilterFields = (searchParams) => searchParamsToScopeFields(searchParams);

/** URL query → 顶栏 scope 补丁（与默认合并） */
export const searchParamsToScopeFields = (searchParams) => {
	const defaults = createStatisticsFilterDefaults();
	const scope = { ...defaults };
	const params = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);

	for (const key of FILTER_SCOPE_PARAM_KEYS) {
		if (!params.has(key)) continue;
		const raw = params.get(key);

		if (DATE_SCOPE_KEYS.includes(key)) {
			scope[key] = parseUrlDate(raw);
			continue;
		}

		if (ARRAY_SCOPE_KEYS.includes(key)) {
			scope[key] = parseCommaList(raw);
			continue;
		}

		if (key === 'dateRelation') {
			scope.dateRelation = raw === 'or' ? 'or' : 'and';
			continue;
		}

		if (key === 'processNodeRelations') {
			scope.processNodeRelations = parseCommaList(raw).map((item) => (item === 'or' ? 'or' : 'and'));
			continue;
		}

		if (key === 'compareYoy') {
			scope.compareYoy = raw !== 'false';
			continue;
		}

		if (key === 'compareMom') {
			scope.compareMom = raw !== 'false';
			continue;
		}

		if (key === 'isCompleted') {
			scope.isCompleted = raw ?? '';
			continue;
		}

		if (key === 'statusFilterAdditive') {
			scope.statusFilterAdditive = raw === 'true';
		}
	}

	if (params.has(COMPLETED_DATE_ANCHOR_URL_KEY)) {
		scope.completedDateAnchor = normalizeTrendAnchor(parseJsonParam(params.get(COMPLETED_DATE_ANCHOR_URL_KEY)));
	}

	if (params.has(DATE_PERIODS_URL_KEY)) {
		const parsed = parseJsonParam(params.get(DATE_PERIODS_URL_KEY));
		if (Array.isArray(parsed) && parsed.length > 1) {
			scope.datePeriods = parsed.map((period) => ({
				startDate: parseUrlDate(period?.startDate) ?? period?.startDate ?? null,
				endDate: parseUrlDate(period?.endDate) ?? period?.endDate ?? null,
			}));
		}
	}

	return normalizeExclusiveOrderStatusFilters(scope);
};

export const hasStatisticsUrlFilterParams = (searchParams) => {
	const params = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);
	return ALL_FILTER_URL_PARAM_KEYS.some((key) => params.has(key));
};

/** 合并筛选参数到 URLSearchParams */
export const mergeStatisticsFilterSearchParams = (searchParams, filters = {}, options = {}) => {
	const params = searchParams instanceof URLSearchParams ? new URLSearchParams(searchParams) : new URLSearchParams(searchParams);

	for (const key of FILTER_SCOPE_PARAM_KEYS) {
		params.delete(key);
	}
	for (const key of LEGACY_FILTER_URL_KEYS_TO_STRIP) {
		params.delete(key);
	}
	params.delete(COMPLETED_DATE_ANCHOR_URL_KEY);
	params.delete(DATE_PERIODS_URL_KEY);
	params.delete(DATE_PERIOD_RELATIONS_URL_KEY);

	const next = filtersToSearchParams(filters, options);
	for (const [key, value] of Object.entries(next)) {
		params.set(key, value);
	}

	params.delete('domain');

	return params;
};

export const buildStatisticsShareUrl = (filters = {}, { origin, pathname } = {}) => {
	const baseOrigin = origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
	const basePath = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/statistics');
	/** 分享链接始终带上起止日期，新窗才能回填顶栏「日期」列（含默认本年） */
	const params = mergeStatisticsFilterSearchParams(new URLSearchParams(), filters, { forceDateScope: true });
	const query = params.toString();
	return query ? `${baseOrigin}${basePath}?${query}` : `${baseOrigin}${basePath}`;
};
